import React, { useState } from "react"
import { graphql, useStaticQuery } from "gatsby"
import { Row, Col, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faAngleDown } from "@fortawesome/free-solid-svg-icons"
import styled from "styled-components"
import { media } from "utils/Media"
import ContainerMax from "components/shared/ContainerMax"
import ContentMax from "components/shared/ContentMax"
import NewsPreview from "components/shared/NewsPreview"
import Button from "components/shared/Button"
import FilterDropdown from "components/shared/FilterDropdown"
import FilterBar from "components/shared/FilterBar"
import Months from "utils/Months"

const NewsListingWrap = styled.div`
	article {
		margin-bottom: 30px;

		p.date {
			font-size: ${props => props.theme.font.size.sm};
			color: ${props => props.theme.colors.black};
			margin: 0;
			display: none;
			
			@media ${media.md} {
				display: block;
			}
		}
		
		p.more {
			text-transform: uppercase;
			line-height: 1;
			margin-top: 1rem;
			
			a {
				font-family: ${props => props.theme.font.family.bold};
				font-size: ${props => props.theme.font.size.sm};
				color: ${props => props.theme.colors.primary};
				text-decoration: none;
			}
		}
	}
	.view-more {
		text-align: center;
		margin-top: 10px;
		button {
			appearance: none;
			background: #fff;
			border: solid 1px #323741;
			height: 50px;
			line-height: 50px;
			padding: 0 40px;
			font-size: 0.875rem;/*14*/
			font-family: ${props => props.theme.font.family.bold};
			color: #323741;
			text-transform: uppercase;
		}
	}
`

const ResetButton = styled.button`
	background: transparent;
	border: 4px solid ${props => props.theme.colors.black};
	padding: .5rem 1.5rem;
	font-family: ${props => props.theme.font.family.bold};
	text-transform: uppercase;

	&:hover {
		background: ${props => props.theme.colors.black};
		color: ${props => props.theme.colors.white};
	}

	@media ${media.lg} {
		position: absolute;
		right: 1rem;
	}
`

const News = (props) => {	
	const postsPerPage = 24;
	const [paginationLimit, setPaginationLimit] = useState(postsPerPage)
	const [filter, setFilter] = useState(0)
	const [filterText, setFilterText] = useState("All")
	const [filterMonth, setFilterMonth] = useState(-1)
	const [filterTextMonth, setFilterTextMonth] = useState("All Months")
	const [filterYear, setFilterYear] = useState(0)
	const [filterTextYear, setFilterTextYear] = useState("All Years")

	/**
	 * Load the next page of articles
	 */
	const loadMore = (e) => {
		e.preventDefault()
		setPaginationLimit(paginationLimit+postsPerPage)
	}
  
	/**
	 * set the filter
	 */
	const updateFilter = (databaseId, name) => {
		setFilter(databaseId)
		setFilterText(name)
	}

	const updateMonth = (id, name) => {
		setFilterMonth(id)
		setFilterTextMonth(name)
	}

	const updateYear = (id, name) => {
		setFilterYear(id)
		setFilterTextYear(name)
	}

	const resetFilters = () => {
		setFilter(0)
		setFilterText("All")
		setFilterMonth(-1)
		setFilterTextMonth("All Months")
		setFilterYear(0)
		setFilterTextYear("All Years")
	}

	const data = useStaticQuery(graphql`
	{
		allWpNewsArticle(
			sort: {fields: date, order: DESC},
			limit: 1000, filter: {status: {eq: "publish"}}
		) {
			edges {
				node {
					uri
					title
					date
					acfNews {
						hidePublishDate
						newsYoutube
						featuredImage45 {
							altText
							localFile {
								childImageSharp {
									gatsbyImageData(width: 536, quality: 85, placeholder: NONE, layout: CONSTRAINED)
								}
							}
						}
					}
					newsCategories {
						nodes {
							name
							databaseId
						}
					}
				}
			}
		}
	}`)

	let posts = data.allWpNewsArticle?.edges
	var printedArticles = 0
	let newsCategories = []
	let newsMonths = []
	let newsYears = []

	//set categories for dropdown
	posts && posts.forEach(({node}) => {
		node.newsCategories && node.newsCategories.nodes.forEach(cat => {
			newsCategories[cat.databaseId] = cat
		})
	})

	//set months for dropdown
	posts && posts.forEach(({node}) => {
		const month = new Date(node.date).getMonth()
		newsMonths[month] = { id: month, name: Months[month] }
	})

	//set years for dropdown
	posts && posts.forEach(({node}) => {
		const year = new Date(node.date).getFullYear()
		newsYears[year] = year
	})

	//filter posts by
	if (filter !== 0) {
		posts = posts && posts.filter(({node}) => {
			const postCategories = node.newsCategories && node.newsCategories.nodes
			return postCategories && postCategories.find(node => filter === node.databaseId)
		})
	}

	//filter by month
	if (filterMonth !== -1) {
		posts = posts && posts.filter(({node}) => {
			const month = new Date(node.date).getMonth()
			return filterMonth === newsMonths[month].id
		})
	}

	// filter by year
	if (filterYear !== 0) {
		posts = posts && posts.filter(({node}) => {
			const year = new Date(node.date).getFullYear()
			return filterYear === year
		})
	}

	return (
		<ContainerMax className="py-4">
			<ContentMax>
				<NewsListingWrap>
					<FilterBar>
						<div className="filter-title">Filter by</div>
						<FilterDropdown>
							<DropdownToggle caret>
									{filterText}
									<FontAwesomeIcon icon={faAngleDown} />
							</DropdownToggle>
							
							<DropdownMenu flip={false}>
								<DropdownItem onClick={() => updateFilter(0, "Year")}>
									All
								</DropdownItem>
								{newsCategories && newsCategories.map((cat, i) => {
									return(
										<DropdownItem key={i} onClick={() => updateFilter(cat.databaseId, cat.name)}>
											{cat.name}
										</DropdownItem>
									)
								})}
							</DropdownMenu>
						</FilterDropdown>

						<FilterDropdown>
							<DropdownToggle caret>
									{filterTextMonth}
									<FontAwesomeIcon icon={faAngleDown} />
							</DropdownToggle>
							
							<DropdownMenu flip={false}>
								<DropdownItem onClick={() => updateMonth(-1, "All Months")}>
									All Months
								</DropdownItem>
								{newsMonths && newsMonths.map((m, i) => {
									return(
										<DropdownItem key={i} onClick={() => updateMonth(m.id, m.name)}>
											{m.name}
										</DropdownItem>
									)
								})}
							</DropdownMenu>
						</FilterDropdown>

						<FilterDropdown>
							<DropdownToggle caret>
									{filterTextYear}
									<FontAwesomeIcon icon={faAngleDown} />
							</DropdownToggle>
							
							<DropdownMenu flip={false}>
								<DropdownItem onClick={() => updateYear(0, "All Years")}>
									All Years
								</DropdownItem>
								{newsYears && newsYears.map((year, i) => {
									return(
										<DropdownItem key={i} onClick={() => updateYear(year, year)}>
											{year}
										</DropdownItem>
									)
								})}
							</DropdownMenu>
						</FilterDropdown>
							
						<ResetButton onClick={resetFilters}>
							Reset
						</ResetButton>

					</FilterBar>
				
					<Row>
						{posts && posts.map((post, i) => {
							const articleType = post.node.acfNews.newsYoutube ? "video" : "news"

							if (paginationLimit > printedArticles) {
								printedArticles++;
								return (
									<Col xs={12} sm={6} md={4} lg={3} key={i} className={`${articleType}`}>
										<NewsPreview 
											data={post} 
											type="NewsListing"
										/>
									</Col>
								)
							}
							return null;
						})}
					</Row>
			
					<div className="view-more">
						{/*do we want to display the 'view more' button?*/}
						{paginationLimit < posts.length &&
							<Button href="#" onClick={(e) => loadMore(e)} color="black">
								<span>View More</span>
							</Button>
						}
					</div>
				</NewsListingWrap>
			</ContentMax>
		</ContainerMax> 
	)
}

export default News